<script setup lang="ts">import { ref as _ref } from 'vue';

import { useRouter } from "vue-router";
import { computed, onMounted, ref, watch } from "vue";
import { SplitterGroup, SplitterPanel, SplitterResizeHandle } from "reka-ui";

import AuditRecord from "@/pages/AuditRecord.vue";

import { DateRange } from "@/models/DateRange";
import { DateValidation } from "@/models/DateValidation";
import { UserSearchResult } from "@/models/UserSearchResult";

import ColumnToggle from "@/components/ColumnToggle.vue";
import StringTooltip from "@/components/Tooltip/StringTooltip.vue";

import { useBrandStore, useCaseFileStore, useCaseStore, useLoggingStore, useMessageStore } from "@/stores";

import { auditService } from "@/services";
import { $can, fromUtc } from "@/services/UtilitiesService";

import { useRootLayout } from "@/hooks/use-root-layout";

import { RsAutocomplete, RsLoading } from "@/components";

import type { AuditHeader } from "@/models/AuditHeader";
import type { ServerResponse } from "@/models/ServerResponse";
import type { Props as AutocompleteProps } from "@/components/Autocomplete.vue";
import type { CursorNavigation } from "@/models/CursorNavigation";

const $router = useRouter();

const brand = computed(() => useBrandStore().selectedBrand);
const casename = computed(() => useCaseStore().selectedCaseName ?? "");
const casefile = computed(() => useCaseFileStore().selectedCaseFile ?? "");
const auditKeyId = computed(() => $router.currentRoute.value.params.auditKeyId as string);

const { layout, sidebarRef } = useRootLayout();

const auditTrailPageSizeStorageKey = computed(() => `AUDIT_TRAIL_PAGE_SIZE-${brand.value}`);
const auditTrailCursorStorageKey = computed(() => `AUDIT_TRAIL_CURSOR${brand.value}`);

const actionTakenList = ref<string[]>([]);

const autocompleteUsernameEntries = ref<UserSearchResult[]>([]);

const cursor = ref(sessionStorage.getItem(auditTrailCursorStorageKey.value) ?? undefined);
const count = ref(
  (() => {
    const stored = sessionStorage.getItem(auditTrailPageSizeStorageKey.value);
    if (stored == null || isNaN(+stored)) return 25;
    return +stored;
  })(),
);

const auditState = ref<
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "done"; records: CursorNavigation<AuditHeader[]>; auditRequest: Record<string, string> }
>({ status: "idle" });

const resetKey = ref("");
const isExtracting = ref(false);

const fromDate = _ref(new DateValidation());
const toDate = _ref(new DateValidation());
const dateRange = new DateRange(fromDate.value as DateValidation, toDate.value as DateValidation);

const openSearchPanel = ref(false);

const isFixedLayout = computed(() => layout.value === "FIXED");
const showAuditDetails = computed(() => !!auditKeyId.value);

const columns = computed<
  Array<
    {
      /** Property name used for searching  */
      fieldPath: string;
      /** Property name used for displaying the value  */
      valueFieldPath?: keyof AuditHeader;
      displayName: string;
      readonly resetValue?: string;
      hiddenInTable?: boolean;
    } & (
      | { type: "text"; value: string; readonly?: boolean; __value?: string }
      | {
          type: "date-range";
          min: string;
          max: string;
          dateRange: DateRange;
          from: { id: string; value: DateValidation };
          to: { id: string; value: DateValidation };
        }
      | {
          type: "select";
          // Vue doesn't allow the placeholder options of select fields to be empty,
          // so an empty value should be a single space, that'll be trimmed when searching
          value: string;
          options: Array<{ value: string; label?: string }>;
        }
      | {
          type: "autocomplete";
          value: string;
          __value?: string;
          modelValue?: string;
          props: AutocompleteProps;
        }
    )
  >
>(() => [
  {
    fieldPath: "key1",
    displayName: "Brand",
    type: "text",
    valueFieldPath: "key2",
    resetValue: brand.value,
    readonly: true,
    get value() {
      return (this.__value || this.resetValue) ?? "";
    },
    set value(v: string) {
      this.__value = v;
    },
  },
  {
    fieldPath: "key2",
    displayName: "Case",
    type: "text",
    valueFieldPath: "key3",
    resetValue: casename.value,
    readonly: casename.value !== "",
    get value() {
      return (this.__value || this.resetValue) ?? "";
    },
    set value(v: string) {
      this.__value = v;
    },
  },
  {
    fieldPath: "key3",
    displayName: "Key 3",
    type: "text",
    valueFieldPath: "key4",
    resetValue: casefile.value,
    readonly: casefile.value !== "",
    get value() {
      return (this.__value || this.resetValue) ?? "";
    },
    set value(v: string) {
      this.__value = v;
    },
  },
  {
    fieldPath: "actionOn",
    displayName: "Date",
    type: "date-range",
    dateRange,
    from: { id: "startDate", value: fromDate.value as DateValidation },
    to: { id: "endDate", value: toDate.value as DateValidation },
    min: DateValidation.minimumDate.toISOString().slice(0, 10),
    get max() {
      const date = new Date();
      const pad = (num: number, length: number) => num.toString().padStart(length, "0");

      return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1, 2)}-${pad(date.getDate(), 2)}`;
    },
  },
  {
    fieldPath: "actionTaken",
    displayName: "Action taken",
    type: "select",
    value: " ",
    resetValue: " ",
    options: actionTakenList.value.map((action) => ({ value: action })),
  },
  {
    fieldPath: "outcome",
    displayName: "Outcome",
    type: "select",
    value: " ",
    resetValue: " ",
    options: [{ value: "Success" }, { value: "Failed" }],
  },
  {
    fieldPath: "username",
    valueFieldPath: "actionBy",
    displayName: "Username",
    type: "autocomplete",
    props: {
      apiEndpoint: `/api/audit/users/${useBrandStore().selectedClient}/:keyword`,
      placeholder: "User Name",
      limit: 20,
      minChars: 1,
      isItemPresent,
      classes: "typeahead",
      getResponse: getAutocompleteUsernameResponse,
    },
    get value() {
      return (this.__value || this.resetValue) ?? "";
    },
    set value(v: string) {
      this.__value = v;
    },
  },
  {
    fieldPath: "ipAddress",
    displayName: "IP address",
    type: "text",
    value: "",
  },
]);

const recordColumn = computed(() => ({
  fieldPath: "linkToRecord",
  displayName: "Record",
  get hiddenInTable(): boolean {
    return hiddenColumns.value.includes(this.fieldPath);
  },
}));

const nonHiddenColumns = computed(() => {
  return columns.value.filter((field) => !hiddenColumns.value.includes(field.fieldPath));
});

const hiddenColumns = ref(columns.value.filter((field) => field.hiddenInTable).map((field) => field.fieldPath));

function back(): void {
  if (auditKeyId.value) {
    viewRecord();
    return;
  }

  if (casefile.value !== "") {
    $router.push({
      name: "casefile",
      params: {
        brand: brand.value,
        casename: casename.value,
        casefile: casefile.value,
      },
    });
    return;
  }

  if (casename.value !== "") {
    $router.push({
      name: "case",
      params: { brand: brand.value, casename: casename.value },
    });
    return;
  }

  $router.push({ name: "brand", params: { brand: brand.value } });
}

function getAuditRequest() {
  const auditRequest: Record<string, string> = {
    fields: JSON.stringify(columns.value.map((col) => col.fieldPath)),
  };

  columns.value.forEach((field) => {
    if (field.type === "date-range") {
      if (!field.dateRange.valid) return;

      if (field.from.value.moment != null) {
        auditRequest[field.from.id] = field.from.value.moment.format();
      }

      if (field.to.value.moment != null) {
        auditRequest[field.to.id] = field.to.value.moment.format();
      }

      return;
    }

    const trimmed = field.value.trim();
    if (trimmed !== "") auditRequest[field.fieldPath] = trimmed;
  });

  return auditRequest;
}

async function loadEntries(): Promise<void> {
  const client = useBrandStore().selectedClient;
  if (client == null) return;

  auditState.value = { status: "loading" };

  try {
    const auditRequest = getAuditRequest();

    const audits = await auditService.GetAuditTrailRecords(client, count.value, {
      ...auditRequest,
      cursor: cursor.value,
    });

    sessionStorage.setItem(auditTrailPageSizeStorageKey.value, count.value.toString());

    auditState.value = { status: "done", records: audits, auditRequest };
  } catch (error) {
    console.error(error);

    auditState.value = { status: "error" };
    useLoggingStore().Error({
      message: "Failed to get audit trail records",
      data: error as object,
    });
  } finally {
    // auto-close form after search
    openSearchPanel.value = false;
  }
}

function viewRecord(auditKeyId?: string): void {
  let name = "brandaudit";
  const params: Record<string, string | undefined> = { brand: brand.value, auditKeyId };

  if (casename.value !== "") {
    name = "caseaudit";
    params.casename = casename.value;
  }

  if (casefile.value !== "") {
    name = "fileaudit";
    params.casefile = casefile.value;
  }

  $router.push({ name, params });
}

function changeNavigation(newCount: number, newCursor: string | undefined) {
  count.value = newCount;
  cursor.value = newCursor;
  loadEntries();
}

function clear(): void {
  columns.value.forEach((field) => {
    if (field.type === "date-range") {
      field.from.value.clear();
      field.to.value.clear();
      field.dateRange.clear();
    } else if (field.type === "autocomplete") {
      field.value = field.resetValue ?? "";
      field.modelValue = "";
    } else field.value = field.resetValue ?? "";
  });

  auditState.value = { status: "idle" };
  resetKey.value = Math.random().toString(36);
}

function search(): Promise<void> {
  cursor.value = undefined;
  return loadEntries();
}

function getAutocompleteUsernameResponse(response: unknown) {
  const { data } = response as ServerResponse<UserSearchResult[]>;
  autocompleteUsernameEntries.value = data;

  return {
    data: data.map((item) => item.displayName),
    totalCount: data[0]?.totalCount,
  };
}

function isItemPresent(name: string) {
  return autocompleteUsernameEntries.value.find((v) => v.displayName === name) != null;
}

async function beginDownload() {
  const client = useBrandStore().selectedClient;
  if (client == null || auditState.value.status !== "done") return;

  try {
    isExtracting.value = true;

    await auditService.ExportAuditTrailRecords(client, auditState.value.auditRequest);

    useMessageStore().updateMessage({
      type: "success",
      title: "Audit Extraction",
      message: "Your request to export the audit trail has been logged, check your jobs list to see its progress.",
      autoHide: true,
      showReload: false,
    });
  } catch {
    useMessageStore().updateMessage({
      type: "error",
      title: "Audit Extraction",
      message:
        "An error occurred while we were requesting the audit export.  If this continues, please contact your support team.",
      autoHide: true,
      showReload: false,
    });
  } finally {
    isExtracting.value = false;
  }
}

function updateHiddenColumns(state: string[]) {
  hiddenColumns.value = state as Array<keyof AuditHeader>;
}

function toggleSearchPanelOpen() {
  if (!isFixedLayout.value) return;
  openSearchPanel.value = !openSearchPanel.value;
}

watch(isFixedLayout, () => (openSearchPanel.value = false));

watch([casename, casefile], () => auditState.value.status !== "idle" && clear(), {
  immediate: true,
});

onMounted(async () => {
  if (!$can("viewAudit")) {
    $router.push({ name: "error", params: { brand: brand.value, code: "403" } });
    return;
  }

  const client = useBrandStore().selectedClient;
  if (client == null) return;

  actionTakenList.value = await auditService
    .getAuditActionTakens(client)
    .then((r) => r.sort())
    .catch((error) => {
      console.error(error);
      useLoggingStore().Error({
        message: "Failed to get audit trail actions",
        data: error as object,
      });

      return [];
    });
});
</script>
<template>
  <Teleport v-if="$router.currentRoute.value.name !== 'brandaudit' || auditKeyId" :to="sidebarRef">
    <StringTooltip side="right" item="Back" #="{ item }">
      <button
        type="button"
        class="tw-btn-md-white tw-grid tw-place-items-center tw-rounded-md tw-bg-gray-300 tw-px-3 tw-py-1.5 tw-text-gray-800"
        :aria-label="item"
        @click.prevent="back"
      >
        <i class="fas fa-arrow-left"></i>
      </button>
    </StringTooltip>
  </Teleport>
  <AuditRecord v-if="showAuditDetails" :audit-key-id="auditKeyId" />
  <template v-else>
    <button
      v-if="isFixedLayout"
      type="button"
      class="tw-btn-green tw-absolute tw-right-3 tw-top-3 tw-z-[100001] tw-aspect-square tw-p-3"
      :aria-pressed="openSearchPanel"
      @click="toggleSearchPanelOpen"
    >
      <span class="tw-sr-only">{{ openSearchPanel ? "Close" : "Open" }} Search Form</span>
      <i aria-hidden="true" class="fas fa-2xl" :class="openSearchPanel ? 'fa-xmark' : 'fa-filter'" />
    </button>
    <SplitterGroup direction="horizontal" auto-save-id="audit-layout-split">
      <SplitterPanel
        as="aside"
        class="tw-flex tw-flex-col tw-bg-white tw-pt-3"
        :min-size="isFixedLayout ? 0 : 25"
        :default-size="isFixedLayout ? 0 : 25"
        :aria-hidden="isFixedLayout && !openSearchPanel"
        :inert="isFixedLayout && !openSearchPanel ? -1 : 0"
        :class="
          isFixedLayout && [
            'tw-absolute tw-inset-0 tw-z-[100000] tw-size-full tw-transition-transform',
            !openSearchPanel && '-tw-translate-x-full',
          ]
        "
      >
        <h1 class="tw-sr-only">Audit Search</h1>
        <form
          :key="resetKey"
          class="tw-flex tw-flex-1 tw-flex-col tw-justify-start tw-gap-3 tw-overflow-y-auto"
          @submit.prevent="search"
          @reset="clear"
        >
          <div class="AuditSearchForm w-full tw-space-y-2 tw-overflow-y-auto tw-px-3">
            <div v-for="col in columns" :key="col.fieldPath" class="max-w-md tw-relative tw-shrink-0">
              <component
                :is="col.type === 'date-range' ? 'div' : 'label'"
                :tabindex="col.type === 'date-range' ? undefined : 0"
                class="tw-block tw-p-1 tw-pl-0 tw-text-sm tw-font-medium"
                :for="col.fieldPath"
              >
                {{ col.displayName }}
              </component>
              <div v-if="col.type == 'text'" class="tw-relative tw-w-full">
                <input
                  :id="col.fieldPath"
                  v-model="col.value"
                  type="text"
                  :readonly="col.readonly"
                  class="tw-w-full tw-rounded-md tw-border tw-border-gray-500 tw-bg-white tw-p-2 tw-opacity-60 read-only:tw-cursor-not-allowed read-only:tw-select-none read-only:tw-bg-black/5 read-only:tw-text-gray-800"
                />
              </div>
              <div v-else-if="col.type == 'date-range'" class="tw-relative tw-flex tw-w-full tw-flex-wrap tw-gap-1">
                <div class="tw-flex tw-flex-1 tw-items-center tw-gap-2">
                  <label tabindex="0" class="tw-text-xs" :for="`${col.fieldPath}__From`">
                    <span class="tw-sr-only">{{ col.displayName }}</span>
                    From
                  </label>
                  <input
                    :id="`${col.fieldPath}__From`"
                    v-model="col.from.value.date"
                    type="date"
                    :min="col.min"
                    :max="col.to.value.date || col.max"
                    :required="!!col.to.value.date"
                    class="invalid:tw-is-input-invalid tw-w-full tw-rounded-md tw-border tw-border-gray-500 tw-bg-white tw-p-2"
                  />
                </div>
                <div class="tw-flex tw-flex-1 tw-items-center tw-gap-2">
                  <label :for="`${col.fieldPath}__To`" class="tw-text-xs">
                    <span class="tw-sr-only">{{ col.displayName }}</span>
                    To
                  </label>
                  <input
                    :id="`${col.fieldPath}__To`"
                    v-model="col.to.value.date"
                    type="date"
                    :max="col.max"
                    :min="col.from.value.date || col.min"
                    :required="!!col.from.value.date"
                    class="invalid:tw-is-input-invalid tw-w-full tw-rounded-md tw-border tw-border-gray-500 tw-bg-white tw-p-2"
                  />
                </div>
              </div>
              <select
                v-else-if="col.type === 'select'"
                :id="col.fieldPath"
                v-model="col.value"
                class="tw-w-full tw-rounded-md tw-border tw-border-gray-500 tw-bg-white tw-p-2"
              >
                <option value=" ">Any</option>
                <option v-for="{ value, label } in col.options" :key="value" :value="value">
                  {{ label ?? value }}
                </option>
              </select>
              <div v-else-if="col.type === 'autocomplete'" class="tw-relative tw-w-full tw-flex-1">
                <RsAutocomplete
                  v-model="col.modelValue"
                  :="col.props"
                  :input-id="col.fieldPath"
                  :input-classes="{
                    '!tw-p-2 !tw-border !tw-w-full !tw-rounded-md !tw-border-gray-500 !tw-bg-white': true,
                  }"
                  @hit="(val) => (col.value = val)"
                />
              </div>
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2">
            <div class="tw-flex-1 tw-text-sm">
              <div v-if="auditState.status === 'loading'" class="tw-flex tw-items-center tw-gap-1 tw-text-sm">
                <i class="fas fa-circle-notch fa-spin fa-sm" />
                Loading...
              </div>
            </div>
            <button type="reset" class="tw-btn-md-gray tw-min-w-20" @click="clear">Reset</button>
            <button type="submit" class="tw-btn-md-green tw-min-w-20" :disabled="auditState.status === 'loading'">
              Search
            </button>
          </div>
        </form>
      </SplitterPanel>
      <SplitterResizeHandle v-if="!isFixedLayout" class="tw-w-4" />
      <SplitterPanel
        as="main"
        class="tw-flex tw-p-4 tw-shadow-inner"
        :min-size="isFixedLayout ? 100 : 50"
        :class="{ 'tw-pl-0': !isFixedLayout }"
      >
        <div
          class="tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col tw-gap-3 tw-overflow-y-auto tw-rounded-sm tw-bg-white tw-p-2"
        >
          <h2 class="tw-px-2 tw-text-lg tw-font-semibold tw-tracking-wide">Results</h2>
          <div
            tabindex="0"
            class="tw-grid tw-w-full tw-grow tw-place-items-center"
            :class="{ 'tw-min-h-full': isFixedLayout }"
          >
            <h1 class="tw-sr-only">Main Content</h1>
            <RsLoading v-if="auditState.status === 'loading'" id="audit-loading" message="Fetching audit records" />
            <h5 v-else-if="auditState.status === 'error'" class="tw-mt-2 tw-text-center tw-font-medium">
              Error executing search
            </h5>
            <h5 v-else-if="auditState.status === 'idle'" class="tw-mt-2 tw-text-center tw-font-medium">
              Set your filters on the left and click Search to load audit records
            </h5>
            <div
              v-else-if="auditState.status === 'done' && auditState.records.items.length > 0"
              class="tw-flex tw-w-full tw-flex-col tw-gap-3 tw-self-start tw-overflow-x-auto"
            >
              <div class="tw-relative tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-gap-2">
                <ColumnToggle
                  :hidden="hiddenColumns"
                  :columns="[...columns, recordColumn]"
                  @on-state-change="updateHiddenColumns"
                />
                <button
                  type="button"
                  class="tw-btn-lg-gray tw-group tw-flex tw-min-w-max tw-items-center tw-justify-start tw-gap-2 disabled:tw-cursor-not-allowed disabled:tw-border disabled:tw-bg-gray-100 disabled:tw-text-gray-600"
                  :disabled="isExtracting"
                  @click="beginDownload"
                >
                  {{ isExtracting ? "Requesting Extract..." : "Request Extract" }}
                </button>
                <div class="tw-flex-1"></div>
                <div class="tw-my-2 tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-gap-2">
                  <div>
                    <select
                      id="pageCount"
                      :value="count"
                      class="tw-rounded-md tw-border tw-border-black/70 tw-p-1"
                      title="Number of items per page"
                      @change="changeNavigation(+($event.target as HTMLInputElement).value, undefined)"
                    >
                      <option>10</option>
                      <option>15</option>
                      <option>20</option>
                      <option>25</option>
                      <option>50</option>
                      <option>75</option>
                      <option>100</option>
                    </select>
                    <label for="pageCount"> items / page</label>
                  </div>
                  <div class="tw-shrink-0 tw-space-x-1">
                    <StringTooltip item="Previous Page" :root="{ disabled: !auditState.records.previousEnabled }">
                      <button
                        type="button"
                        class="tw-aspect-square tw-size-6 tw-rounded-full tw-bg-white tw-p-1 tw-text-black"
                        aria-label="Previous Page"
                        :disabled="!auditState.records.previousEnabled"
                        :class="
                          !auditState.records.previousEnabled
                            ? 'tw-cursor-not-allowed tw-opacity-70'
                            : 'hover:tw-brightness-75'
                        "
                        @click.prevent="changeNavigation(count, auditState.records.previousToken)"
                      >
                        <i :aria-hidden="true" class="fas fa-chevron-left" />
                      </button>
                    </StringTooltip>
                    <StringTooltip item="Next Page" :root="{ disabled: !auditState.records.nextEnabled }">
                      <button
                        type="button"
                        class="tw-aspect-square tw-size-6 tw-rounded-full tw-bg-white tw-p-1 tw-text-black"
                        aria-label="Next Page"
                        :disabled="!auditState.records.nextEnabled"
                        :class="
                          !auditState.records.nextEnabled
                            ? 'tw-cursor-not-allowed tw-opacity-70'
                            : 'hover:tw-brightness-75'
                        "
                        @click.prevent="changeNavigation(count, auditState.records.nextToken)"
                      >
                        <i :aria-hidden="true" class="fas fa-chevron-right" />
                      </button>
                    </StringTooltip>
                  </div>
                </div>
              </div>
              <table class="tw-w-full tw-self-start tw-rounded-lg tw-border">
                <thead>
                  <tr
                    tabindex="0"
                    class="z-1 tw-sticky -tw-top-1 tw-h-7 tw-divide-x tw-border-b [&>*]:tw-bg-gray-100 [&>*]:tw-p-2"
                  >
                    <template
                      v-for="{ fieldPath, displayName, hiddenInTable } in [...nonHiddenColumns, recordColumn]"
                      :key="fieldPath"
                    >
                      <th v-if="hiddenInTable !== true" scope="col" tabindex="0">
                        {{ displayName }}
                      </th>
                    </template>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, idx) in auditState.records.items"
                    :key="item.id"
                    tabindex="0"
                    style="cursor: pointer"
                    :class="{ 'tw-bg-gray-200': idx % 2 === 0 }"
                    class="tw-h-7 tw-divide-x tw-border-b last:tw-border-b-0 [&>*]:tw-p-2 [&>*]:hover:tw-bg-gray-300 [&>*]:focus:tw-bg-gray-300"
                  >
                    <td
                      v-for="{ fieldPath, type, valueFieldPath } in nonHiddenColumns"
                      :key="`${valueFieldPath}_${fieldPath}`"
                      tabindex="0"
                      @click="viewRecord(item.id?.toString() ?? '')"
                    >
                      {{
                        type === "date-range"
                          ? fromUtc(item[valueFieldPath ?? (fieldPath as keyof AuditHeader)] as string, {
                              dateStyle: "medium",
                              timeStyle: "medium",
                            })
                          : item[valueFieldPath ?? (fieldPath as keyof AuditHeader)]
                      }}
                    </td>
                    <td v-if="!recordColumn.hiddenInTable" tabindex="0">
                      <router-link
                        v-if="item.key3"
                        class="tw-underline tw-underline-offset-2"
                        :to="`/${item.key2}/${item.key3}${item.key4 ? `/${item.key4}` : ''}`"
                      >
                        Record
                      </router-link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <h5 v-else class="tw-mt-2 tw-text-center tw-font-medium">There were no results returned for that search</h5>
          </div>
        </div>
      </SplitterPanel>
    </SplitterGroup>
  </template>
</template>
<style scoped>
.number-field::-webkit-inner-spin-button,
.number-field::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.w-15p {
  width: 15%;
}

.w-12p {
  width: 12%;
}

.table-sm {
  font-size: 0.7rem;
}
</style>
